/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IEveFormSelectData } from './eve-form-select.data';
import { fieldwidth, IEveFormSelectProps } from './eve-form-select.props.autogenerated';
import { FormContext, IFieldValidation, IFormField } from '../eve-form/context/FormContext';
import { normalizeName } from '../../utilities/normalizeName';

export interface IEveFormSelectViewProps extends IEveFormSelectProps<IEveFormSelectData> {
    registeredField: IFormField;
    handleChange: () => {};
}

/**
 *
 * EveFormSelect component
 * @extends {React.PureComponent<IEveFormSelectProps<IEveFormSelectData>>}
 */
class EveFormSelect extends React.PureComponent<IEveFormSelectProps<IEveFormSelectData>> {
    public static contextType = FormContext;
    public declare context: React.ContextType<typeof FormContext>;

    constructor(props: IEveFormSelectProps<IEveFormSelectData>) {
        super(props);
        this.state = { value: '' };
        this.handleChange = this.handleChange.bind(this);
        this.reset = this.reset.bind(this);
    }

    /**
     * resets this field.
     * @returns void
     */
    public reset() {
        const {
            config: { values }
        } = this.props;

        let listItem = values ? values[0] : null;
        values?.forEach(item => {
            if (item.isDefault) {
                listItem = item;
            }
        });

        const registeredField = this.context.fields[this.props.config.name ? normalizeName(this.props.config.name) : ''];
        registeredField.errors = [];
        registeredField.value = listItem?.value ? listItem.value : listItem?.displayValue ? listItem.displayValue : '';
    }

    /**
     * fires when this component has mounted; prepares validation and registers this field to context.
     * @returns void
     */
    public componentDidMount() {
        const {
            resources: { errorMessage },
            config: { name, defaultIsInvalid, values, friendlyName }
        } = this.props;

        const validationArray = [];

        let value = values ? values[0] : null;
        values?.forEach(item => {
            if (item.isDefault) {
                value = item;
            }
        });

        if (defaultIsInvalid) {
            if (value) {
                if (value.value) {
                    const defaultInvalidValidation: IFieldValidation = {
                        regex: '',
                        errorMessage: errorMessage,
                        isRequiredValidation: false,
                        invalidSelection: value.value
                    };
                    validationArray.push(defaultInvalidValidation);
                } else {
                    const defaultInvalidValidation: IFieldValidation = {
                        regex: '',
                        errorMessage: errorMessage,
                        isRequiredValidation: false,
                        invalidSelection: value.displayValue
                    };
                    validationArray.push(defaultInvalidValidation);
                }
            }
        }

        this.context.registerField({
            isRequired: true,
            name: name ? name : '',
            validation: validationArray,
            value: value?.value ? value.value : value?.displayValue ? value.displayValue : '',
            errors: [],
            friendly: friendlyName ? friendlyName : '',
            reset: this.reset
        });
    }

    /**
     * Updates this fields value in context.
     * @param e : React.ChangeEvent<HTMLInputElement>
     */
    private handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
        this.context.updateField(
            this.props.config.name ? this.props.config.name : '',
            e.target.value,
            this.props.config.friendlyName ? this.props.config.friendlyName : ''
        );
    }

    /**
     * Returns bootstrap styling based on enum value from module config. This is replicated in all eve-form input modules due to autogenerated definitions.
     * @returns string
     */
    private getWidth() {
        switch (this.props.config.fieldwidth) {
            case fieldwidth.halfWidth:
                return 'col-6';
            case fieldwidth.thirdWidth:
                return 'col-4';
            case fieldwidth.twoThirdWidth:
                return 'col-8';
            case fieldwidth.fullWidth:
            default:
                return 'col-12';
        }
    }

    /**
     * This renders the module.
     * @returns JSX.Element | Null
     */
    public render(): JSX.Element | null {
        const registeredField = this.context.fields[this.props.config.name ? normalizeName(this.props.config.name) : ''];
        if (!registeredField) {
            return null;
        }

        return (
            <div className={`form-group ${this.getWidth()}`}>
                {this.props.renderView({
                    ...this.props,
                    registeredField: registeredField,
                    handleChange: this.handleChange
                })}
            </div>
        );
    }
}

export default EveFormSelect;
